// @flow
import { createReducer } from 'redux-act';

import type {
  AvailabilityEventData,
  SuggestionEventData,
  Details as State,
  Prices,
} from '../../../types';
import {
  add,
  reset,
  setBilling,
  setAvailability,
  setTimeoutErrors,
  flagOptimistic,
  unflagOptimistic,
  setPhase,
} from '../../actions/results';

import { formatSuggestionPrices } from '../../helpers/pricer';

const initialState: State = {};

function resetHandler(): State {
  return initialState;
}

function addHandler(state: State, suggestions: SuggestionEventData) {
  const newSuggestions = suggestions.filter(({ fqdn }) => !state[fqdn]);

  if (!newSuggestions.length) {
    return state;
  }

  const newState = { ...state };
  newSuggestions.forEach(suggestion => {
    newState[suggestion.fqdn] = {
      tld: suggestion.tld,
      fqdn: suggestion.fqdn,
      domain: suggestion.domain,
      provided: suggestion.provided,
      phase: suggestion.phase || 'golive',
      corporate: suggestion.corporate,
      allowsLang: suggestion.allow_lang,
      restriction: suggestion.restriction,
      error: false,
    };
  });

  return newState;
}

function setAvailabilityHandler(
  state: State,
  availability: AvailabilityEventData,
): State {
  if (
    !state[availability.fqdn] ||
    state[availability.fqdn].availability === availability.availability
  ) {
    return state;
  }

  return {
    ...state,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [availability.fqdn]: {
      ...state[availability.fqdn],
      availability: availability.availability,
      premium: !!availability.premium,
    },
  };
}

type BillingEventData = {
  fqdn: string,
  prices: {} | Prices,
  ...
};
function setBillingHandler(state: State, billing: BillingEventData): State {
  if (!state[billing.fqdn] || !state[billing.fqdn].availability) {
    return state;
  }

  const fqdn = billing.fqdn;
  const suggestion = state[billing.fqdn];

  let forceDisplayRestriction = false;
  let update = {};
  let empty = true;
  if (Array.isArray(billing.prices.products)) {
    empty = false;
    const createProduct = billing.prices.products.find(
      product => product.process === 'create',
    );

    if (createProduct) {
      forceDisplayRestriction =
        !!createProduct.prices[0].options.force_registration_display;
    }

    update = formatSuggestionPrices({
      phase: suggestion.phase,
      // $FlowFixMe[prop-missing] - we already ruled out the `{||}` case.
      prices: billing.prices,
    });
  }

  // Catch a race condition when
  // The `billing` event for this domain arrives empty
  // after a successful individual lookup for this domain (such as IDN).
  if (suggestion.prices && empty) {
    return state;
  }

  return {
    ...state,
    [fqdn]: {
      ...suggestion,
      ...update,
      forceDisplayRestriction,
      error: empty && suggestion.availability !== 'lang_required',
    },
  };
}

function setTimeoutErrorsHandler(state: State) {
  const timeouts = Object.keys(state).filter(
    fqdn =>
      !!(
        (!state[fqdn].availability || !state[fqdn].prices) &&
        !state[fqdn].error
      ),
  );

  if (!timeouts.length) {
    return state;
  }

  const newState = { ...state };
  timeouts.forEach(fqdn => {
    newState[fqdn] = {
      ...newState[fqdn],
      error: true,
    };
  });
  return newState;
}

function flagOptimisticHandler(state: State, fqdn: string) {
  if (!state[fqdn] || state[fqdn].optimistic) {
    return state;
  }

  return {
    ...state,
    [fqdn]: {
      ...state[fqdn],
      optimistic: true,
    },
  };
}

function unflagOptimisticHandler(state: State, fqdn: string) {
  if (!state[fqdn] || !state[fqdn].optimistic) {
    return state;
  }

  return {
    ...state,
    [fqdn]: {
      ...state[fqdn],
      optimistic: false,
    },
  };
}

function setPhaseHandler(
  state: State,
  { fqdn, phase }: { fqdn: string, phase: string, ... },
) {
  if (!state[fqdn] || state[fqdn].phase === phase) {
    return state;
  }

  return {
    ...state,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [fqdn]: {
      ...state[fqdn],
      phase,
    },
  };
}

export default (createReducer(
  {
    [add.toString()]: addHandler,
    [reset.toString()]: resetHandler,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [setBilling.toString()]: setBillingHandler,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [setAvailability.toString()]: setAvailabilityHandler,
    [setTimeoutErrors.toString()]: setTimeoutErrorsHandler,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [flagOptimistic.toString()]: flagOptimisticHandler,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [unflagOptimistic.toString()]: unflagOptimisticHandler,
    // $FlowFixMe[incompatible-type] TODO: fix type
    [setPhase.toString()]: setPhaseHandler,
  },
  initialState,
): {
  (): (state: State, payload?: mixed, meta?: mixed) => State,
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<State>,
  options: () => mixed,
  ...
});
